import { useEffect, useState } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import { API_URL } from '../lib/auth'
import {
  CheckCircle,
  Copy,
  Check,
  ArrowRight,
  Download,
  Key,
  AlertCircle,
  Loader2
} from 'lucide-react'

interface SessionData {
  id: string
  status: string
  paymentStatus: string
  customerEmail: string
  amountTotal: number
  currency: string
}

interface LicenseData {
  key: string
  type: string
  status: string
}

export function PurchaseSuccess() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [session, setSession] = useState<SessionData | null>(null)
  const [license, setLicense] = useState<LicenseData | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const fetchSession = async () => {
      const params = new URLSearchParams(window.location.search)
      const sessionId = params.get('session_id')

      if (!sessionId) {
        setError('No session ID found')
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${API_URL}/api/stripe/session/${sessionId}`)
        const data = await response.json()

        if (data.success) {
          setSession(data.session)
          setLicense(data.license)
        } else {
          setError(data.message || 'Failed to fetch purchase details')
        }
      } catch (err) {
        console.error('Error fetching session:', err)
        setError('An error occurred while fetching your purchase details')
      } finally {
        setLoading(false)
      }
    }

    fetchSession()
  }, [])

  const copyLicenseKey = () => {
    if (license?.key) {
      navigator.clipboard.writeText(license.key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <div className="noise-overlay" />
        <MeshGradient />
        <Navbar currentPage="purchase" />

        <main className="relative z-10 pt-32 pb-20 px-6">
          <div className="max-w-xl mx-auto text-center">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading your purchase details...</p>
          </div>
        </main>

        <Footer />
      </div>
    )
  }

  if (error) {
    return (
      <div className="relative min-h-screen">
        <div className="noise-overlay" />
        <MeshGradient />
        <Navbar currentPage="purchase" />

        <main className="relative z-10 pt-32 pb-20 px-6">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-zinc-400 mb-8">{error}</p>
            <a
              href="/purchase"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
            >
              Try again
            </a>
          </div>
        </main>

        <Footer />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="purchase" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">Thank you for your purchase!</h1>
            <p className="text-zinc-400">
              Your Nexatice Pro subscription has been confirmed
            </p>
          </div>

          {license && (
            <div className="bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 rounded-2xl p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Key className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-white font-medium">Your License Key</h2>
                  <p className="text-zinc-500 text-sm">Save this key - you'll need it when Pro launches</p>
                </div>
              </div>

              <div className="bg-zinc-800/50 rounded-lg p-4 flex items-center justify-between gap-4">
                <code className="text-lg font-mono text-amber-400 tracking-wider">
                  {license.key}
                </code>
                <button
                  onClick={copyLicenseKey}
                  className="flex-shrink-0 p-2 rounded-lg bg-zinc-700/50 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>

              <div className="mt-4 p-4 bg-amber-500/5 border border-amber-500/10 rounded-lg">
                <p className="text-amber-200/80 text-sm">
                  Your license is for <strong>Nexatice Pro Monthly</strong>. 
                  The license has been linked to your email address. You can view and manage all your licenses in your{' '}
                  <a href="/dashboard" className="text-amber-400 underline hover:text-amber-300">Dashboard</a>.
                </p>
              </div>
            </div>
          )}

          {session && (
            <div className="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 rounded-xl p-5 mb-8">
              <h3 className="text-white font-medium mb-4">Order Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Email</span>
                  <span className="text-zinc-300">{session.customerEmail}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Amount Paid</span>
                  <span className="text-zinc-300">
                    €{session.amountTotal?.toFixed(2)} {session.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Status</span>
                  <span className="text-emerald-400 capitalize">{session.paymentStatus}</span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 mb-8">
            <h3 className="text-white font-medium mb-4">What happens next?</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-400 text-xs font-bold">1</span>
                </div>
                <div>
                  <p className="text-zinc-300">Your license is linked to your email</p>
                  <p className="text-zinc-500 text-sm">View all your licenses in the Dashboard → License tab</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-400 text-xs font-bold">2</span>
                </div>
                <div>
                  <p className="text-zinc-300">Activate in the desktop app</p>
                  <p className="text-zinc-500 text-sm">Open Settings → License in the app and enter your key</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-400 text-xs font-bold">3</span>
                </div>
                <div>
                  <p className="text-zinc-300">Enjoy Pro features</p>
                  <p className="text-zinc-500 text-sm">Use on up to 3 devices with a single license</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#download"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black font-medium hover:from-amber-400 hover:to-orange-400 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Nexatice
            </a>
            <a
              href="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 transition-colors"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

